import type { CoverLetterDto } from "@reactive-resume/dto";
import { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { redirect } from "react-router";

import { queryClient } from "@/client/libs/query-client";
import { findCoverLetterById } from "@/client/services/cover-letter";
import { useBuilderStore } from "@/client/stores/builder";
import { useCoverLetterStore } from "@/client/stores/cover-letter";

export const CoverLetterBuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);

  const coverLetter = useCoverLetterStore((state) => state.coverLetter);
  const title = useCoverLetterStore((state) => state.coverLetter.title);

  const updateCoverLetterInFrame = useCallback(() => {
    const message = { type: "SET_COVER_LETTER", payload: coverLetter.data };

    setImmediate(() => {
      frameRef?.contentWindow?.postMessage(message, "*");
    });
  }, [frameRef?.contentWindow, coverLetter.data]);

  useEffect(() => {
    if (!frameRef) return;
    frameRef.addEventListener("load", updateCoverLetterInFrame);
    return () => {
      frameRef.removeEventListener("load", updateCoverLetterInFrame);
    };
  }, [frameRef]);

  useEffect(updateCoverLetterInFrame, [coverLetter.data]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-12">
        <iframe
          ref={setFrameRef}
          title={coverLetter.id}
          src="/artboard/cl-builder"
          className="h-0 min-h-0 w-full flex-1 rounded-2xl bg-neutral-100"
        />
      </div>
    </>
  );
};

export const coverLetterBuilderLoader: LoaderFunction<CoverLetterDto> = async ({ params }) => {
  try {
    const id = params.id!;

    const coverLetter = await queryClient.fetchQuery({
      queryKey: ["cover-letter", { id }],
      queryFn: () => findCoverLetterById({ id }),
    });

    useCoverLetterStore.setState({ coverLetter });
    useCoverLetterStore.temporal.getState().clear();

    return coverLetter;
  } catch (err: any) {
    const status = err?.status ?? err?.response?.status;
    if (status === 401 || status === 403) return redirect("/auth/login");
    if (status === 400 || status === 422) {
      throw new Response(
        JSON.stringify({
          title: "This cover letter has invalid data",
          message: err?.message ?? "Validation failed",
          id: params.id,
        }),
        { status: 422, headers: { "Content-Type": "application/json" } },
      );
    }
    return redirect("/dashboard");
  }
};
